/**
 * Distance/visibility conversion utilities.
 * Uses meters as the base (SI) unit internally.
 */

import { Units } from '../../units';

const MI_TO_M = 1609.344;
const FT_TO_M = 0.3048;
const NM_TO_M = 1852;
const KM_TO_M = 1000;

/**
 * Convert a distance/height value to meters (base unit).
 */
export function distanceToMeters(value: number, fromUnit: string): number {
  switch (fromUnit.toLowerCase().trim()) {
    case 'mi':
    case 'miles':
      return value * MI_TO_M;
    case 'ft':
    case 'feet':
    case 'foot':
      return value * FT_TO_M;
    case 'nm':
    case 'nmi':
    case 'nautical miles':
      return value * NM_TO_M;
    case 'km':
    case 'kilometers':
    case 'kilometres':
      return value * KM_TO_M;
    case 'm':
    case 'meters':
    case 'metres':
      return value;
    default:
      return value;
  }
}

/**
 * Convert distance from meters for visibility/large distances.
 */
export function distanceFromMeters(
  valueMeters: number,
  targetSystem: Units,
): { value: number; unit: string } {
  switch (targetSystem) {
    case Units.MARITIME:
      return { value: valueMeters / NM_TO_M, unit: 'NM' };
    case Units.IMPERIAL:
      return { value: valueMeters / MI_TO_M, unit: 'mi' };
    case Units.METRIC:
    default:
      return { value: valueMeters / KM_TO_M, unit: 'km' };
  }
}

/**
 * Convert height from meters (for wave heights, altitude, etc.).
 */
export function heightFromMeters(
  valueMeters: number,
  targetSystem: Units,
): { value: number; unit: string } {
  switch (targetSystem) {
    case Units.IMPERIAL:
      return { value: valueMeters / FT_TO_M, unit: 'ft' };
    case Units.METRIC:
    case Units.MARITIME:
    default:
      return { value: valueMeters, unit: 'm' };
  }
}

/**
 * Convert a distance from a source unit to the user's preferred system.
 */
export function convertDistance(
  value: number,
  fromUnit: string,
  targetSystem: Units,
): { value: number; unit: string } {
  const meters = distanceToMeters(value, fromUnit);
  return distanceFromMeters(meters, targetSystem);
}

/**
 * Convert a height/wave height from a source unit to the user's preferred system.
 */
export function convertHeight(
  value: number,
  fromUnit: string,
  targetSystem: Units,
): { value: number; unit: string } {
  const meters = distanceToMeters(value, fromUnit);
  return heightFromMeters(meters, targetSystem);
}
